import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, HelpCircle, Calendar, ExternalLink, Loader, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import SEOHead from './SEOHead';
import { generateReportListSEO } from '../utils/seoUtils';
import './ReportList.css';

const ReportList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    limit: 20,
    has_next: false,
    has_prev: false
  });
  const [statistics, setStatistics] = useState({
    total: 0,
    true: 0,
    false: 0,
    partial: 0,
    unverified: 0
  });

  useEffect(() => {
    fetchReports(1);
  }, []);

  const fetchReports = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/reports?page=${page}&limit=${pagination.limit}`);
      setReports(response.data.reports);
      setPagination(response.data.pagination);
      setStatistics(response.data.statistics);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchReports(newPage);
      // Scroll to top when changing pages
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'True':
        return <CheckCircle className="result-icon true" />;
      case 'False':
        return <XCircle className="result-icon false" />;
      case 'Partially True':
        return <AlertCircle className="result-icon partial" />;
      case 'Unverified':
        return <HelpCircle className="result-icon unverified" />;
      default:
        return null;
    }
  };

  const getResultClass = (result) => {
    switch (result) {
      case 'True':
        return 'true';
      case 'False':
        return 'false';
      case 'Partially True':
        return 'partial';
      case 'Unverified':
        return 'unverified';
      default:
        return '';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="report-list">
        <div className="container">
          <div className="loading-container">
            <Loader className="spinner large" />
            <p>Loading reports...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-list">
        <div className="container">
          <div className="error-container">
            <XCircle className="error-icon" />
            <h2>Error Loading Reports</h2>
            <p>{error}</p>
            <button className="button-primary" onClick={fetchReports}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Generate SEO data for reports list page
  const seoData = generateReportListSEO();

  return (
    <div className="report-list">
      {/* SEO Meta Tags */}
      <SEOHead {...seoData} />
      
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="page-header">
            <motion.h1 
              className="page-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Verification <span className="gradient-text">Reports</span>
            </motion.h1>
            
            <motion.p 
              className="page-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
             
            </motion.p>
          </div>

          {reports.length === 0 ? (
            <motion.div 
              className="empty-state glass-effect"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <FileText className="empty-icon" />
              <h3>No Reports Yet</h3>
              <p>Start verifying news content to see reports here.</p>
              <Link to="/" className="button-primary">
                Verify News
              </Link>
            </motion.div>
          ) : (
            <>
              <motion.div 
                className="reports-stats glass-effect"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="stat-item">
                  <div className="stat-number">{statistics.total}</div>
                  <div className="stat-label">Total Reports</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-number">{statistics.true}</div>
                  <div className="stat-label true">Verified True</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-number">{statistics.false}</div>
                  <div className="stat-label false">Verified False</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-number">{statistics.partial}</div>
                  <div className="stat-label partial">Partially True</div>
                </div>

                <div className="stat-item">
                  <div className="stat-number">{statistics.unverified}</div>
                  <div className="stat-label unverified">Unverified</div>
                </div>
              </motion.div>

              <motion.div 
                className="reports-grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                {reports.map((report, index) => (
                  <motion.div
                    key={report.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 + 0.1 * index }}
                  >
                    <Link to={`/report/${report.id}`} className="report-card glass-effect">
                      <div className="report-card-header">
                        <div className={`result-badge ${getResultClass(report.result)}`}>
                          {getResultIcon(report.result)}
                          <span>{report.result}</span>
                        </div>
                        
                        <div className="report-date">
                          <Calendar size={14} />
                          <span>{formatDate(report.created_at)}</span>
                        </div>
                      </div>

                      <div className="report-card-content">
                        <h3 className="report-card-title">
                          {truncateText(report.title, 100)}
                        </h3>
                        
                        <p className="report-card-summary">
                          {report.summary || 'No summary available'}
                        </p>
                      </div>

                      <div className="report-card-footer">
                        <span className="read-more">
                          <ExternalLink size={16} />
                          View Full Report
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>

              {/* Pagination Controls */}
              {pagination.total_pages > 1 && (
                <motion.div 
                  className="pagination-container"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                >
                  <div className="pagination glass-effect">
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={!pagination.has_prev}
                    >
                      <ChevronLeft size={20} />
                      Previous
                    </button>
                    
                    <div className="pagination-info">
                      <span className="pagination-text">
                        Page {pagination.current_page} of {pagination.total_pages}
                      </span>
                      <span className="pagination-count">
                        Showing {reports.length} of {pagination.total_count} reports
                      </span>
                    </div>
                    
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={!pagination.has_next}
                    >
                      Next
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </motion.div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ReportList; 